import { BackSide, BoxGeometry, Mesh, MeshBasicMaterial, PlaneGeometry, Scene } from 'three';

/**
 * Procedural room for image-based lighting (no HDR download).
 *
 * What polished acetate reflects on a webcam is a lit interior: light walls, a
 * brighter ceiling with its lamps, a window or softbox to one side and — right
 * below the camera — the laptop screen. The reflections of those large, soft,
 * bright surfaces along the rounded edges and over the front are what makes
 * acetate read as acetate rather than painted plastic; an enclosure that is
 * black between a few panels renders the frame matte and flat between two
 * hot spots. The scene is rendered once into a PMREM cube map at start-up.
 *
 * Every surface is emissive (the cube map sees it directly). Dimensions are
 * arbitrary (only directions matter); the panels face the origin. +Z is
 * towards the camera / viewer, +Y up.
 */
export function createStudioEnvironment(): Scene {
  const scene = new Scene();

  // Walls: a dim, slightly warm interior (linear ≈ 0.16) — never pure black in a reflection.
  const walls = new Mesh(new BoxGeometry(40, 40, 40), new MeshBasicMaterial({ color: 0x6f6d6a, side: BackSide }));
  scene.add(walls);

  // Ceiling (brighter than the walls). Below the frame there is no floor but
  // the wearer's face and clothes: a warm skin bounce, so the downward-facing
  // rounded edges of the rims are lit like they are on a real face.
  addPanel(scene, 24, 24, 0xffffff, 0.45, [0, 16, 0]);
  addPanel(scene, 18, 18, 0xc9b3a4, 0.5, [0, -14, 0]);

  // Key softbox: high, front-left of the wearer's face (camera side) — high
  // enough that a front facing the camera does not mirror it across its width.
  addPanel(scene, 8, 6, 0xfff5ea, 2.0, [-7, 9, 8]);
  // Ceiling lamp strip: the thin bright streak that runs along the brow bar and the temples.
  addPanel(scene, 22, 0.9, 0xffffff, 2.4, [0, 11, 3]);
  // Fill panel, camera right, cooler and dimmer.
  addPanel(scene, 6, 6, 0xe6eefc, 1.0, [10, 3, 8]);
  // Window behind the camera and the laptop screen just below it: the two
  // things a webcam sees mirrored in sunglasses. Small and modest: a front is
  // nearly flat, so a large bright panel behind the camera washes the whole
  // front grey instead of leaving a highlight.
  addPanel(scene, 6, 4, 0xfdfdff, 1.0, [0, 5, 12]);
  addPanel(scene, 5, 3, 0xf4f6ff, 0.8, [0, -3, 12]);
  // Rim / kicker lights behind, well to the sides: edge highlights on the
  // temples in three-quarter views. Kept dim and off the axis — a temple seen
  // edge-on from the front mirrors what is straight behind the wearer, and a
  // bright panel there turned the temples white.
  addPanel(scene, 4, 8, 0xffffff, 0.7, [13, 4, -6]);
  addPanel(scene, 4, 8, 0xfff4ea, 0.5, [-13, 3, -6]);

  return scene;
}

function addPanel(scene: Scene, w: number, h: number, color: number, intensity: number, position: [number, number, number]): void {
  const mat = new MeshBasicMaterial({ color });
  mat.color.multiplyScalar(intensity);
  const mesh = new Mesh(new PlaneGeometry(w, h), mat);
  mesh.position.set(...position);
  mesh.lookAt(0, 0, 0); // emitters face the centre, where the cube map is rendered
  scene.add(mesh);
}
